<script lang="ts">
  import Fooji from "@fooji/microsites";
  import { countryStore } from "../lib/stores/country";
  import { setLoading, nextStep } from "../lib/stores/route";
  import { getReceiptLocations, POSTypes, type ReceiptLocation } from "../lib/helpers/receiptData";
  import { onMount } from "svelte";
  import { isDemo } from "../lib/stores/mode";
  import { copy } from "../lib/stores/copy";
  import InvalidFieldMessage from "./Input/InvalidFieldMessage.svelte";
  import SearchableDropdown from "./SearchableDropdown.svelte";
  import { notify } from "../lib/helpers/bugsnag";

  export let showUploader: () => void;
  export let toggleType: () => void;

  let orderNumber = "";
  let orderDate = "";
  let locationId = -1;
  let locations: ReceiptLocation[] = [];
  
  let fieldMessages: {
    orderNumber?: string;
    orderDate?: string;
    location?: string;
  } = {};
  
  let validateOnChange = false;
  let codeGenerated = false;
  let errorMessage = "";

  onMount(async () => {
    setLoading(true);
    locations = await getReceiptLocations($countryStore!);
    setLoading(false);
  });

  const validate = () => {
    fieldMessages = {};

    if (!orderNumber.trim()) {
      fieldMessages.orderNumber = $copy.receipt.receiptData.validations.orderNumberRequired;
    }

    const minimumDate = document.location.hostname.endsWith(".littlecaesars.com") ? new Date("2025-11-03") : new Date("2025-06-01");

    if (!orderDate) {
      fieldMessages.orderDate = $copy.receipt.receiptData.validations.orderDateRequired;
    } else if (new Date(orderDate).getTime() > Date.now()) {
      fieldMessages.orderDate = $copy.receipt.receiptData.validations.orderDateInFuture;
    } else if (new Date(orderDate).getTime() > Date.now() - 15 * 60 * 1000) {
      fieldMessages.orderDate = $copy.receipt.receiptData.validations.orderDateTooRecent;
    } else if (new Date(orderDate).getTime() < new Date(minimumDate).getTime()) {
      fieldMessages.orderDate = $copy.receipt.receiptData.validations.orderDateTooOld;
    }

    const pos = locations.find(({ locationNumber }) => locationNumber === locationId)?.posTypeAsOfCampaignStart.pos;

    if (!pos) {
      fieldMessages.location = $copy.receipt.receiptData.validations.locationRequired;
    }

    validateOnChange = true;
    return Object.keys(fieldMessages).length === 0 ? pos : null;
  };

  const submitReceiptData = async () => {
    const pos = validate();
    
    if (!pos) {
      return;
    }

    // Clear any previous error messages
    errorMessage = "";
    codeGenerated = false;

    try {
      setLoading(true);

      if (!isDemo()) {
        // Convert datetime-local to ISO format with Z timezone
        const isoDate = new Date(orderDate).toISOString();
        await Fooji.UserSettings.save({
          lc_cod_code: false,
          entry_token: false,
          file_upload: false,
          lc_cod_order_id: orderNumber,
          lc_cod_order_date: isoDate,
          lc_cod_location_id: locationId
        })
      }

      if (pos === POSTypes.CVCore) {
        // just proceed to the next step, we don't need an upload for this
        nextStep();
      } else if (pos === POSTypes.NonCV) {
        // We need to proceed to the upload step
        showUploader();
      } else {
        // Show message that code was generated
        codeGenerated = true;
      }
    } catch (error) {
      console.error('Error submitting receipt data:', error);
      notify({
        errorMessage: 'Failed to save receipt information',
        error 
      });
      errorMessage = $copy.receipt.receiptData.saveFailedErrorMessage;
    } finally {
      setLoading(false);
    }
  }
</script>


<div class="max-w-[1100px] mx-auto laptop:pb-8">
  <div class="bg-black/50 rounded-[12px] px-[16px] py-[32px] mt-8">
    <div class="w-full">
      <div class="text-center">
        <h1 class="font-MrEavesXLSanNarOTUltra flex flex-col items-center text-primary whitespace-normal leading-[0.75] uppercase mb-[14px] tablet:block tablet:whitespace-nowrap laptop:mb-[20px] {$copy.receipt.titleTextClasses}">
          {@html $copy.receipt.title}
        </h1>
      </div>
      <div class="text-center">
        <div class="font-MrEavesXLSanNarOTUltra text-white font-black leading-[0.9] uppercase text-[16px] mb-[16px] mobileS:text-[20px] tablet:text-[24px] laptop:text-[26px] desktop:text-[30px] desktopLg:text-[37px]">
          {@html $copy.receipt.receiptData.completeReceiptInfo}
        </div>
      </div>
      <form on:submit|preventDefault>
        <div class="flex justify-between flex-wrap">
          <div class="relative w-full mb-[26px] tablet:w-[48%]">
            <label
              for="orderNumber"
              class="font-MrEavesXLSanNarOTUltra relative text-[11px] inline-flex font-black text-primary mb-[4px] leading-[1.2] uppercase mobile:text-[12px] tablet:text-[12px] laptop:text-[15px]"
            >
              {$copy.receipt.receiptData.orderNumber}<span class="reg text-[0.7em] align-top">*</span>
            </label>
            <input
              type="text"
              id="orderNumber"
              name="orderNumber"
              placeholder="{$copy.receipt.receiptData.orderNumberPlaceholder}"
              bind:value={orderNumber}
              on:input={validateOnChange ? validate : () => {}}
              autocomplete="off"
              class="std-field__input font-MrEavesXLSanNarOT relative text-[20px] px-[14px] w-full h-[42px] bg-grey text-input border-solid border-transparent border-1 rounded-[8px] z-[10] outline-none laptop:h-[46px]"
            />
            <InvalidFieldMessage message={fieldMessages.orderNumber} />
          </div>
          <div class="relative w-full mb-[26px] tablet:w-[48%]">
            <label
              for="orderDate"
              class="font-MrEavesXLSanNarOTUltra relative text-[11px] inline-flex font-black text-primary mb-[4px] leading-[1.2] uppercase mobile:text-[12px] tablet:text-[12px] laptop:text-[15px]"
            >
              {$copy.receipt.receiptData.orderDate}<span class="reg text-[0.7em] align-top">*</span>
            </label>
            <input
              type="datetime-local"
              id="orderDate"
              name="orderDate"
              bind:value={orderDate}
              max={'9999-12-31T23:59'}
              on:input={validateOnChange ? validate : () => {}}
              class="std-field__input font-MrEavesXLSanNarOT relative text-[20px] px-[14px] w-full h-[42px] bg-grey border-solid border-transparent border-1 rounded-[8px] z-[10] outline-none laptop:h-[46px]"
              class:text-input={Boolean(orderDate)}
              class:text-gray-400={!Boolean(orderDate)}
            />
            <InvalidFieldMessage message={fieldMessages.orderDate} />
          </div>
        </div>
        <div class="flex justify-between flex-wrap">
          <div class="relative w-full mb-[26px] tablet:w-[48%]">
            <label
              for="location"
              class="font-MrEavesXLSanNarOTUltra relative text-[11px] inline-flex font-black text-primary mb-[4px] leading-[1.2] uppercase mobile:text-[12px] tablet:text-[12px] laptop:text-[15px]"
            >
              {$copy.receipt.receiptData.location}<span class="reg text-[0.7em] align-top">*</span>
            </label>
            <SearchableDropdown
              id="location"
              name="location"
              placeholder={$copy.receipt.receiptData.locationPlaceholder}
              options={locations.map((location) => {
                const labelComponents = [location.addressLineOne, location.addressLineTwo, location.city, location.state, location.postalCode];
                const label = labelComponents.filter(Boolean).join(", ");

                return {
                  value: location.locationNumber,
                  label
                }
              })}
              bind:selectedValue={locationId}
              validateOnChange={validateOnChange}
              onValidate={validate}
            />
            <InvalidFieldMessage message={fieldMessages.location} />
          </div>
        </div>
        
        {#if codeGenerated}
          <div class="bg-primary/20 border border-primary rounded-lg p-4 mb-6">
            <div class="font-MrEavesXLSanNarOT text-white text-center text-[16px] mobile:text-[18px] tablet:text-[20px] leading-[1]">
              {@html $copy.receipt.receiptData.codeGenerated.message}
            </div>
            <div class="text-center mt-3">
              <a 
                href="/" 
                on:click|preventDefault={toggleType}
                class="font-MrEavesXLSanNarOTUltra text-primary underline text-[16px] mobile:text-[18px] tablet:text-[20px] hover:text-white transition-colors"
              >
                {$copy.receipt.receiptData.codeGenerated.linkText}
              </a>
            </div>
          </div>
        {/if}
        
        {#if errorMessage}
          <div class="bg-red-500/20 border border-red-500 rounded-lg p-4 mb-6">
            <div class="font-MrEavesXLSanNarOT text-white text-center text-[16px] mobile:text-[18px] tablet:text-[20px]">
              {errorMessage}
            </div>
          </div>
        {/if}
        
        <div class="flex justify-center mt-[32px]">
          <button
            type="submit"
            on:click={submitReceiptData}
            class="std-button font-MrEavesXLSanNarOTUltra relative inline-flex justify-center items-center text-[18px] text-white px-[32px] uppercase h-[48px] min-w-[132px] bg-dark border-solid border-white border-2 no-underline uppercase transition cursor-pointer overflow-hidden"
          >
            <span class="relative z-[1]">{$copy.receipt.receiptData.submitReceipt}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</div>

<style>
  /* Custom styling for datetime-local input */
  input[type="datetime-local"] {
    color-scheme: dark;
  }
</style>
